import {
  type ComponentProps,
  type PropsWithChildren,
  useContext,
  useEffect,
} from "react";
import classNames from "classnames";

import { Action, Category } from "@/constants/events";
import StoreLocatorContext from "@/context/StoreLocatorContext";
import { useEventTracker } from "@/hooks/useEventTracker";

import Button from "@/components/botanic/Button/Button";
import DraggableContent, {
  DraggableContentContext,
  SnapPositions,
} from "@/components/botanic/DraggableContent";

const SwitchToMapViewButton = ({ onClick }: { onClick?: () => void }) => {
  const { updateScreenPosition, screenPosition } = useContext(
    DraggableContentContext,
  );
  const { publishEvent } = useEventTracker();

  return screenPosition === SnapPositions.Full ? (
    <div
      className={classNames(
        "bg-white px-md py-sm shadow-low", // visual styles
        "fixed bottom-0 left-0 right-0 z-30", // positioning styles
      )}
    >
      <Button
        data-testid="switch-to-map-button"
        variant="secondary"
        iconFilePath="map.svg"
        onClick={() => {
          onClick?.();
          updateScreenPosition(SnapPositions.Minimized);
          publishEvent({
            action: Action.click,
            category: Category.finder,
            label: "map view",
          });
        }}
      >
        switch to map view
      </Button>
    </div>
  ) : null;
};

const MobileContentLayout = ({ children }: PropsWithChildren) => {
  const { filtersOpen } = useContext(StoreLocatorContext);
  const { updateScreenPosition, screenPosition } = useContext(
    DraggableContentContext,
  );

  useEffect(() => {
    updateScreenPosition(filtersOpen ? SnapPositions.Full : SnapPositions.Half);
  }, [filtersOpen, updateScreenPosition]);

  const { publishEvent } = useEventTracker();
  useEffect(() => {
    // fire an event when full screen but not due to filters
    if (screenPosition === SnapPositions.Full && !filtersOpen) {
      publishEvent({
        action: Action.click,
        category: Category.finder,
        label: "list view",
      });
    }
  }, [screenPosition, publishEvent, filtersOpen]);

  return children;
};

export default ({
  children,
  ...draggableProps
}: PropsWithChildren<ComponentProps<typeof DraggableContent>>) => (
  <DraggableContent {...draggableProps} data-testid="content-layout-mobile">
    <MobileContentLayout>{children}</MobileContentLayout>
    <SwitchToMapViewButton />
  </DraggableContent>
);
